import React, { useEffect, useState } from 'react'
import braintreeHostedFields from 'braintree-web/hosted-fields'
import { asModal } from 'common/Modal/Modal'
import firebase from 'firebase/app'
import { withFormik } from 'formik'
import { useTranslation, Trans } from 'react-i18next'
import { braintree } from 'variables'
import * as Sentry from '@sentry/browser'
import Form, {
  FormButton,
  FormError,
  FormLabel,
  FormInputLabel,
  FormikFormInput as FormInput,
  FormFieldset,
  FormRow
} from '../../../../common/components/Form'
import Icon from '../../../../common/components/Icon'
import Label, { LabelSpacer } from '../../../../common/components/Label/Label'
import Text, { Paragraph } from '../../../../common/components/Text'
import Loader from '../../common/Loader'
import { ModalTitle } from '../../../../common/Modal/Modal'
import styled from 'react-emotion'
import Row from '../../../../common/components/Row'
import { color } from '../../../../common/style'
import { Avatar } from '../../../../common/components/Avatar'
import UserMessage from './UserMessage'
import Hr from 'common/components/Hr'
import { ScrollArea } from 'common/components';

const useBraintree = () => {
  const { t } = useTranslation()
  const [error, setError] = useState()
  const [hostedFields, hostedFieldsLoaded] = useState()

  useEffect(() => {
    ;(async () => {
      const authorization = await fetch(braintree.clientToken, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      }).then(r => r.json())

      const instance = await braintreeHostedFields
        .create({
          authorization: authorization.clientToken,
          styles: {
            input: {
              'font-size': '16px',
              'font-weight': 500,
              'letter-spacing': '-0.2px' // match Suisse a bit better
            },
            '.invalid': { color: color.red }
          },
          fields: {
            number: {
              selector: '#number',
              placeholder: t('settings.payment.placeholders.cardNumber')
            },
            cvv: {
              selector: '#cvv',
              placeholder: t('settings.payment.placeholders.cvv')
            },
            expirationDate: {
              selector: '#expirationDate ',
              placeholder: t('settings.payment.placeholders.expiryDate')
            }
          }
        })
        .catch(error => {
          console.error('client-token', error)
          setError(error.message || 'Unknown')
        })
      hostedFieldsLoaded(instance)
    })()

    return () => {
      hostedFields && hostedFields.teardown()
    }
  }, [])

  return [hostedFields, error]
}

const BraintreeField = styled(FormLabel)({
  label: 'BraintreeField',
  iframe: { margin: '0 !important' },
  div: { display: 'none' }
})

const PaymentModal = ({ plan, user, close }) => {
  const { t } = useTranslation()
  const [braintreeHF] = useBraintree()
  // const [braintreeHF, braintreeError] = useBraintree()  // TODO: handle error
  const [discount, setDiscount] = useState()

  return (
    <>
      <ModalTitle>
        <Trans
          i18nKey="settings.payment.title"
          values={{ plan: t(`plans.${plan.id}.name`) }}
        >
          <Text>
            Upgrade to <Text bold>Plan</Text>
          </Text>
        </Trans>
      </ModalTitle>

      {/* Loader */}
      {!braintreeHF && (
        <Row xLarge>
          <Loader />
        </Row>
      )}

      <ScrollArea>
        <PaymentForm
          plan={plan}
          user={user}
          close={close}
          braintreeHF={braintreeHF}
          setDiscount={setDiscount}
          discount={discount}
        />
      </ScrollArea>
    </>
  )
}

const PaymentFormContainer = withFormik({
  mapPropsToValues: () => ({
    discountCode: '',
    number: '',
    expirationDate: '',
    cvv: ''
  }),

  validate: async (
    { discountCode },
    { discount, setDiscount, braintreeHF }
  ) => {
    const errors = {}

    const braintreeState = braintreeHF.getState()
    Object.keys(braintreeState.fields).forEach(key => {
      const field = braintreeState.fields[key]

      if (field.isEmpty) {
        errors[key] = `settings.payment.errors.${key}Required`
      } else if (!field.isValid) {
        errors[key] = `settings.payment.errors.${key}Invalid`
      }
    })

    if (discountCode) {
      if (discountCode.length !== 8) {
        setDiscount(null)
        errors.discountCode = 'settings.payment.errors.discountInvalid'
      } else {
        if (!discount || discount.id !== discountCode) {
          discount = await firebase
            .database()
            .ref(`/discountCodes/${discountCode}`)
            .once('value')
            .then(snap => snap.val())
          setDiscount(discount)
          if (!discount) {
            errors.discountCode = 'settings.payment.errors.discountInvalid'
          }
        }
      }
    }

    if (Object.keys(errors).length) {
      throw errors
    }

    return true
  },

  handleSubmit: (values, { props, setSubmitting, setErrors, setStatus }) => {
    setSubmitting(true)

    return props.braintreeHF
      .tokenize()
      .then(payload =>
        fetch(braintree.subscription, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          method: 'post',
          body: JSON.stringify({
            nonce: payload.nonce,
            plan: props.plan.id,
            discountCode: values.discountCode,
            email: props.user.email,
            name: props.user.displayName,
            userId: props.user.uid
          })
        })
      )
      .then(response => {
        setSubmitting(false)
        return response.json().then(response => {
          if (response.success) {
            setStatus('success')
          } else {
            props.showAlert(null, props.t('account.upgrade.error'))
            Sentry.withScope(scope => {
              scope.setExtra('response', response)
              Sentry.captureMessage('Braintree Error Response')
            })

            console.error('Error Response', response)
            setErrors({ number: response.message })
          }
        })
      })
      .catch(err => {
        console.error('processing error', err)
        setSubmitting(false)
        // {name: "BraintreeError", code: "HOSTED_FIELDS_FIELDS_EMPTY",
        // message: "All fields are empty. Cannot tokenize empty card fields.", type: "CUSTOMER"}
        if (err.code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
          console.log(err)
          setErrors({
            number: `settings.payment.errors.${'number'}Required`,
            cvv: `settings.payment.errors.${'cvv'}Required`,
            expirationDate: `settings.payment.errors.${'expirationDate'}Required`
          })
        } else if (err.code === 'HOSTED_FIELDS_FIELDS_INVALID') {
          // details.invalidFieldKeys == ['number', 'cvv', "expirationDate"]
          const errors = {}
          err.details.invalidFieldKeys.forEach(field => {
            errors[field] = `settings.payment.errors.${field}Invalid`
          })
          setErrors(errors)
        } else {
          setErrors({
            number: err.code,
            cvv: err.code,
            expirationDate: err.code
          })
        }
      })
  }
})

const useBraintreeFormikLink = (
  braintreeHF,
  setFieldValue,
  setFieldTouched
) => {
  useEffect(() => {
    if (!braintreeHF) return

    braintreeHF.on('notEmpty', event => {
      setFieldValue(event.emittedBy, 'invalid')
    })
    braintreeHF.on('empty', event => {
      setFieldValue(event.emittedBy, '')
    })
    braintreeHF.on('validityChange', function(event) {
      var field = event.fields[event.emittedBy]
      if (field.isValid) {
        setFieldValue(event.emittedBy, 'valid')
      } else {
        setFieldValue(event.emittedBy, 'invalid')
      }
    })
    braintreeHF.on('blur', event => {
      setFieldTouched(event.emittedBy)
    })
  }, [braintreeHF, setFieldValue, setFieldTouched])
}

const PaymentForm = PaymentFormContainer(
  ({
    user,
    plan,
    braintreeHF,
    discount,
    errors,
    status,
    isValid,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    close
  }) => {
    const { t } = useTranslation()

    // Make sure Formik knows when Braintree changes
    useBraintreeFormikLink(braintreeHF, setFieldValue, setFieldTouched)

    if (status === 'success')
      return (
        <>
          <Form>
            <UserMessage>
              <Avatar name={user.displayName} large />
              <Text>
                <Trans
                  i18nKey="settings.cancelSub.canceled"
                  values={{ plan: t(`plans.${plan.id}.name`) }}
                >
                  You’re on <Text bold>Plan</Text>
                </Trans>
              </Text>
            </UserMessage>

            <FormButton xLarge onClick={close} type="button">
              {t('settings.cancelSub.continue')}
              <LabelSpacer />
              <Icon icon="arrow-right" />
            </FormButton>
          </Form>
        </>
      )

    const price =
      plan.price - (discount ? plan.price * (discount.discount / 100) : 0)

    return (
      <Form
        onSubmit={handleSubmit}
        style={!braintreeHF ? { height: 0, overflow: 'hidden' } : {}}
      >
        <FormFieldset>
          <FormInputLabel>{t('settings.payment.cardNumber')}</FormInputLabel>
          <BraintreeField invalid={'number' in errors} id="number" />
          <FormError name="number" />
        </FormFieldset>

        <FormFieldset>
          <FormRow>
            <div>
              <FormInputLabel>{t('settings.payment.expiry')}</FormInputLabel>
              <BraintreeField
                invalid={'expirationDate' in errors}
                id="expirationDate"
              />
            </div>
            <div>
              <FormInputLabel>{t('settings.payment.cvv')}</FormInputLabel>
              <BraintreeField invalid={'cvv' in errors} id="cvv" />
            </div>
          </FormRow>
          <FormError name="expirationDate" />
          <FormError name="cvv" />
        </FormFieldset>

        <FormFieldset>
          <FormInputLabel>{t('settings.payment.coupon')}</FormInputLabel>
          <FormInput invalid={'discountCode' in errors} name="discountCode" />
          <FormError name="discountCode" />
          {discount && (
            <FormInput
              disabled
              value={t('settings.payment.discount', discount)}
            />
          )}
        </FormFieldset>

        <Hr />

        <Paragraph small grey>
          <Trans
            i18nKey={
              discount
                ? 'settings.payment.noteDiscount'
                : 'settings.payment.note'
            }
            values={{ price, fullPrice: plan.price, ...discount }}
          >
            You'll be changed <b>$$</b> monthly then <b>$$$</b>.
          </Trans>
        </Paragraph>

        {process.env.REACT_APP_CONFIG === 'staging' && (
          <Label danger xLarge align="left">
            <Text multiline>
              <Text bold>Warning</Text> staging is connected to the real payment
              system!
            </Text>
          </Label>
        )}

        <FormFieldset>
          <FormButton type="submit" disabled={!isValid}>
            <Text>{t('settings.payment.continue', { price })}</Text>
            <LabelSpacer />
            <Icon icon="arrow-right" />
          </FormButton>
        </FormFieldset>
      </Form>
    )
  }
)

export default asModal({ small: true })(PaymentModal)
